/**
 * Graph management using ngraph
 */
import createGraph from 'ngraph.graph';
import ngraphCreateLayout from 'ngraph.forcelayout';
import ngraphPath from 'ngraph.path';
import { PHYSICS_SETTINGS, MIN_MATCH } from '../config.js';

export class ArtistGraph {
  constructor() {
    this.graph = createGraph();
    this.layout = null;
    this.pathFinder = null;
    this.init();
  }

  init() {
    // Create layout with physics settings that references the graph
    const settings = {
      ...PHYSICS_SETTINGS,
      nodeMass: (nodeId) => PHYSICS_SETTINGS.nodeMass(nodeId, this.graph)
    };
    this.layout = ngraphCreateLayout(this.graph, settings);

    this.pathFinder = ngraphPath.aGreedy(this.graph, {
      distance(fromNode, toNode, link) {
        return 1 - link.data.match || 0;
      }
    });
  }

  /**
   * Add a node to the graph
   * @param {Object} artist - Artist data
   * @param {Object} position - Optional initial position {x, y}
   * @returns {Object} The created node
   */
  addNode(artist, position = null) {
    const node = this.graph.addNode(artist.id, artist);
    if (position) {
      this.layout.setNodePosition(node.id, position.x, position.y);
    }
    return node;
  }

  /**
   * Get a node by ID
   * @param {number} id - Node ID
   * @returns {Object|null} The node or null
   */
  getNode(id) {
    return this.graph.getNode(id);
  }

  /**
   * Add an edge between two nodes
   * @param {Object} node1 - First node
   * @param {Object} node2 - Second node
   * @param {Object} similarityData - Similarity data including match value
   * @returns {Object} The created link
   */
  addEdge(node1, node2, similarityData) {
    let link = this.graph.getLink(node1.id, node2.id) ||
               this.graph.addLink(node1.id, node2.id, similarityData);
    link.data.showCount = 120;

    if (similarityData.match) {
      this.layout.getSpring(link).length = 80 - 50 * similarityData.match;
    }

    return link;
  }

  /**
   * Remove a node and its edges
   * @param {number} id - Node ID to remove
   */
  removeNode(id) {
    const node = this.graph.getNode(id);
    if (!node) return;

    // Clean up graphics
    if (node.links) {
      const links = [...node.links];
      links.forEach(link => {
        if (link.data.line) {
          link.data.line.destroy();
          link.data.line = undefined;
        }
        this.graph.removeLink(link);
      });
    }

    if (node.data.circle) {
      node.data.circle.destroy();
      node.data.circle = undefined;
    }

    if (node.data.labelDiv) {
      node.data.labelDiv.remove();
      node.data.labelDiv = undefined;
    }

    this.graph.removeNode(id);
  }

  /**
   * Clear the entire graph
   */
  clear() {
    this.graph.clear();
  }

  /**
   * Iterate over all nodes
   * @param {Function} callback - Function to call for each node
   */
  forEachNode(callback) {
    this.graph.forEachNode(callback);
  }

  /**
   * Iterate over all links
   * @param {Function} callback - Function to call for each link
   */
  forEachLink(callback) {
    this.graph.forEachLink(callback);
  }

  /**
   * Iterate over linked nodes
   * @param {number} nodeId - Source node ID
   * @param {Function} callback - Function to call for each linked node
   */
  forEachLinkedNode(nodeId, callback) {
    this.graph.forEachLinkedNode(nodeId, callback);
  }

  /**
   * Get total node count
   * @returns {number} Number of nodes
   */
  getNodeCount() {
    return this.graph.getNodesCount();
  }

  /**
   * Get node position from layout
   * @param {number} nodeId - Node ID
   * @returns {Object} Position {x, y}
   */
  getNodePosition(nodeId) {
    return this.layout.getNodePosition(nodeId);
  }

  /**
   * Set node position in layout
   * @param {number} nodeId - Node ID
   * @param {number} x - X coordinate
   * @param {number} y - Y coordinate
   */
  setNodePosition(nodeId, x, y) {
    this.layout.setNodePosition(nodeId, x, y);
  }

  /**
   * Pin/unpin a node
   * @param {Object} node - The node to pin
   * @param {boolean} isPinned - Whether to pin or unpin
   */
  pinNode(node, isPinned) {
    this.layout.pinNode(node, isPinned);
  }

  /**
   * Step the physics simulation forward
   */
  step() {
    this.layout.step();
  }

  /**
   * Find shortest path between two nodes
   * @param {number} fromId - Start node ID
   * @param {number} toId - End node ID
   * @returns {Array} Array of nodes in the path
   */
  findPath(fromId, toId) {
    return this.pathFinder.find(fromId, toId);
  }

  /**
   * Get link between two nodes
   * @param {number} fromId - First node ID
   * @param {number} toId - Second node ID
   * @returns {Object|null} The link or null
   */
  getLink(fromId, toId) {
    return this.graph.getLink(fromId, toId);
  }

  /**
   * Get link position
   * @param {string} linkId - Link ID
   * @returns {Object} Position with from and to coordinates
   */
  getLinkPosition(linkId) {
    return this.layout.getLinkPosition(linkId);
  }
}
